/**
 * Reagent's sound vocabulary, authored as data.
 *
 * Every sound is synthesised at runtime from the specs below rather than
 * loaded from a file. Three reasons, in order of how much they mattered:
 *
 *  1. `04-art-direction.md` asks for "chunky tactile switches ... bouncy analog dials".
 *     That is 1970s electromechanical noise — clicks, thumps, relay chatter,
 *     klaxons — which is exactly what subtractive synthesis is good at, and
 *     what sample libraries are full of poor imitations of.
 *  2. A cue is then plain data, so the registry that maps events to sounds can
 *     be unit-tested in node with no audio hardware and no fixtures. The FX
 *     layer earns its exhaustiveness check the same way.
 *  3. No binary assets, so the repo stays diffable and the build stays small.
 *
 * A cue is a stack of layers played together. Layers are deliberately crude —
 * one oscillator or one noise burst, one filter, one envelope — because the
 * character comes from stacking a pitched body under a noisy transient, not
 * from any single layer being clever.
 */

export type LayerKind = "tone" | "noise";

export interface CueLayer {
  readonly kind: LayerKind;
  /** Start frequency in Hz. For noise, the filter centre instead. */
  readonly freq: number;
  /** Sweeps to this by the end of the layer. Omit for a steady pitch. */
  readonly freqEnd?: number;
  readonly wave?: OscillatorType;
  readonly duration: number;
  readonly gain: number;
  /** Seconds of fade-in. A hard zero is what makes a click a click. */
  readonly attack?: number;
  /** Seconds to wait before this layer starts, for two-part hits. */
  readonly delay?: number;
  readonly filter?: { readonly type: BiquadFilterType; readonly freq: number; readonly q?: number };
  /** Repeats of the whole layer, for klaxons and bubbling. */
  readonly repeats?: number;
  readonly repeatGap?: number;
}

export interface CueSpec {
  readonly layers: readonly CueLayer[];
  /**
   * How many of this cue may sound in one throttle window. Packet movement
   * fires up to a dozen times a tick, and a dozen identical blips is not
   * twelve times the information — it is a buzz.
   */
  readonly maxPerWindow?: number;
  /** Cue-relative level, so the mix is tunable without touching envelopes. */
  readonly level?: number;
}

export type CueName =
  // Interface
  | "switchThrow"
  | "cardPick"
  | "cardPlay"
  | "refused"
  | "uiClick"
  | "purchase"
  | "hardwareInstalled"
  | "hardwareDismantled"
  // Simulation
  | "packetMove"
  | "merge"
  | "phaseConvert"
  | "separate"
  | "reactionExothermic"
  | "reactionEndothermic"
  | "heat"
  | "overheatWarning"
  | "explode"
  | "pressure"
  | "burst"
  | "corrode"
  | "leak"
  | "poison"
  | "sludge"
  | "regulate"
  | "objective"
  | "polluted"
  | "contaminated"
  | "verminRisk"
  | "infested"
  | "powerOutage"
  | "structuralDisaster"
  | "hazardPenalty"
  | "hazardCleared"
  | "runCleared"
  | "runFailed";

export const CUES: Record<CueName, CueSpec> = {
  /** The big lever. A relay clack over a cabinet thump — the whole point of `03-hazards.md` §2. */
  switchThrow: {
    level: 0.9,
    layers: [
      { kind: "noise", freq: 2600, duration: 0.035, gain: 0.5, filter: { type: "bandpass", freq: 2600, q: 1.2 } },
      { kind: "tone", freq: 150, freqEnd: 62, wave: "square", duration: 0.11, gain: 0.35, delay: 0.012 },
    ],
  },

  cardPick: {
    level: 0.45,
    layers: [{ kind: "noise", freq: 4200, duration: 0.022, gain: 0.3, filter: { type: "highpass", freq: 2200 } }],
  },

  /** Card meeting bench: a paper slap with a wooden knock under it. */
  cardPlay: {
    level: 0.8,
    layers: [
      { kind: "noise", freq: 1500, duration: 0.05, gain: 0.42, filter: { type: "bandpass", freq: 1500, q: 0.8 } },
      { kind: "tone", freq: 210, freqEnd: 96, wave: "triangle", duration: 0.13, gain: 0.3 },
    ],
  },

  /** A refusal costs nothing, so it buzzes rather than punishes. */
  refused: {
    level: 0.6,
    layers: [{ kind: "tone", freq: 150, freqEnd: 120, wave: "square", duration: 0.16, gain: 0.22, filter: { type: "lowpass", freq: 900 } }],
  },

  uiClick: {
    level: 0.4,
    layers: [{ kind: "noise", freq: 3200, duration: 0.018, gain: 0.24, filter: { type: "bandpass", freq: 3200, q: 1.5 } }],
  },

  purchase: {
    level: 0.7,
    layers: [
      { kind: "tone", freq: 660, wave: "triangle", duration: 0.09, gain: 0.28 },
      { kind: "tone", freq: 990, wave: "triangle", duration: 0.16, gain: 0.24, delay: 0.07 },
    ],
  },

  /** Hardware snapping into place during Setup phase: a sharp click with a mechanical settling tone underneath. */
  hardwareInstalled: {
    level: 0.75,
    layers: [
      { kind: "noise", freq: 3600, duration: 0.03, gain: 0.35, attack: 0.002, filter: { type: "highpass", freq: 2400 } },
      { kind: "tone", freq: 500, freqEnd: 300, wave: "triangle", duration: 0.15, gain: 0.28, delay: 0.02 },
    ],
  },

  /** Hardware reversal during Setup phase: a duller click with a brief downward tone, distinct from the sharper install snap. */
  hardwareDismantled: {
    level: 0.65,
    layers: [
      { kind: "noise", freq: 1200, duration: 0.04, gain: 0.25, filter: { type: "bandpass", freq: 1200, q: 1.1 } },
      { kind: "tone", freq: 400, freqEnd: 150, wave: "square", duration: 0.12, gain: 0.22, delay: 0.01, filter: { type: "lowpass", freq: 900 } },
    ],
  },

  /**
   * One packet, one hop. Very quiet and hard-throttled: this is texture that
   * tells you the line is running, not an event you are meant to count.
   */
  packetMove: {
    level: 0.3,
    maxPerWindow: 2,
    layers: [{ kind: "tone", freq: 520, freqEnd: 700, wave: "sine", duration: 0.07, gain: 0.13, attack: 0.008 }],
  },

  merge: {
    level: 0.6,
    maxPerWindow: 2,
    layers: [
      { kind: "tone", freq: 430, wave: "sine", duration: 0.12, gain: 0.2 },
      { kind: "tone", freq: 645, wave: "sine", duration: 0.18, gain: 0.16, delay: 0.045 },
    ],
  },

  /** Phase conversion: vapour and liquid diverge. A hiss with two tones moving opposite directions. */
  phaseConvert: {
    level: 0.65,
    maxPerWindow: 2,
    layers: [
      { kind: "noise", freq: 2800, duration: 0.08, gain: 0.2, attack: 0.008, filter: { type: "highpass", freq: 1600 } },
      { kind: "tone", freq: 850, freqEnd: 500, wave: "sine", duration: 0.15, gain: 0.18, delay: 0.02 },
      { kind: "tone", freq: 320, freqEnd: 550, wave: "sine", duration: 0.15, gain: 0.18, delay: 0.02 },
    ],
  },

  /** Stream separation: material partitioning into main and waste. A percussive click followed by a clean tone, distinct from phaseConvert's hiss and diverging pair. */
  separate: {
    level: 0.65,
    maxPerWindow: 2,
    layers: [
      { kind: "noise", freq: 1600, duration: 0.04, gain: 0.25, attack: 0.002, filter: { type: "bandpass", freq: 1600, q: 1.2 } },
      { kind: "tone", freq: 600, wave: "sine", duration: 0.16, gain: 0.2, attack: 0.01, delay: 0.03 },
    ],
  },

  /** Exothermic: a pressure hiss. This is the sound that should worry you. */
  reactionExothermic: {
    level: 0.75,
    maxPerWindow: 2,
    layers: [
      { kind: "noise", freq: 1800, duration: 0.28, gain: 0.3, attack: 0.02, filter: { type: "bandpass", freq: 1800, q: 0.7 } },
      { kind: "tone", freq: 320, freqEnd: 190, wave: "sawtooth", duration: 0.22, gain: 0.14, filter: { type: "lowpass", freq: 1200 } },
    ],
  },

  /** Endothermic: the same event drawing heat in. Falls rather than hisses. */
  reactionEndothermic: {
    level: 0.7,
    maxPerWindow: 2,
    layers: [{ kind: "tone", freq: 400, freqEnd: 240, wave: "triangle", duration: 0.3, gain: 0.2, attack: 0.03 }],
  },

  heat: {
    level: 0.5,
    maxPerWindow: 1,
    layers: [{ kind: "tone", freq: 240, freqEnd: 340, wave: "sine", duration: 0.22, gain: 0.14, attack: 0.05 }],
  },

  /**
   * §2.1's two-tick grace period, made audible. A two-blast klaxon, because a
   * warning you can only see is a warning you miss while reading your hand.
   */
  overheatWarning: {
    level: 1,
    layers: [
      {
        kind: "tone",
        freq: 620,
        freqEnd: 430,
        wave: "square",
        duration: 0.2,
        gain: 0.24,
        repeats: 2,
        repeatGap: 0.1,
        filter: { type: "lowpass", freq: 1600 },
      },
    ],
  },

  explode: {
    level: 1,
    layers: [
      { kind: "noise", freq: 400, duration: 0.55, gain: 0.6, filter: { type: "lowpass", freq: 900 } },
      { kind: "tone", freq: 130, freqEnd: 42, wave: "sawtooth", duration: 0.5, gain: 0.4, filter: { type: "lowpass", freq: 700 } },
    ],
  },

  pressure: {
    level: 0.55,
    maxPerWindow: 2,
    layers: [{ kind: "tone", freq: 96, freqEnd: 128, wave: "square", duration: 0.14, gain: 0.2, filter: { type: "lowpass", freq: 500 } }],
  },

  burst: {
    level: 0.95,
    layers: [
      { kind: "noise", freq: 3000, duration: 0.09, gain: 0.55, filter: { type: "highpass", freq: 1200 } },
      { kind: "tone", freq: 200, freqEnd: 70, wave: "square", duration: 0.24, gain: 0.3 },
    ],
  },

  /** Grit, not tone: acid eating wall (§2.3). */
  corrode: {
    level: 0.45,
    maxPerWindow: 1,
    layers: [{ kind: "noise", freq: 5200, duration: 0.13, gain: 0.2, attack: 0.02, filter: { type: "highpass", freq: 3600 } }],
  },

  leak: {
    level: 0.7,
    layers: [
      { kind: "tone", freq: 700, freqEnd: 300, wave: "sine", duration: 0.2, gain: 0.2, repeats: 3, repeatGap: 0.13 },
    ],
  },

  poison: {
    level: 0.75,
    layers: [
      { kind: "tone", freq: 340, freqEnd: 150, wave: "sawtooth", duration: 0.4, gain: 0.22, filter: { type: "lowpass", freq: 1100 } },
      { kind: "noise", freq: 900, duration: 0.3, gain: 0.16, attack: 0.05, filter: { type: "bandpass", freq: 900, q: 1.4 } },
    ],
  },

  /** §2.5, and it should sound as unpleasant as it is: thick, slow bubbles. */
  sludge: {
    level: 0.7,
    layers: [
      { kind: "tone", freq: 120, freqEnd: 210, wave: "sine", duration: 0.16, gain: 0.24, repeats: 3, repeatGap: 0.15, filter: { type: "lowpass", freq: 600 } },
    ],
  },

  /** Thermal regulation in process: a steady mechanical hum, distinct from heat's
   * climbing sine and phaseConvert's hiss. A brief square-wave pulse conveys
   * ongoing machinery operation without alarm. */
  regulate: {
    level: 0.6,
    layers: [
      { kind: "tone", freq: 450, wave: "square", duration: 0.18, gain: 0.18, attack: 0.01, filter: { type: "lowpass", freq: 1200 } },
    ],
  },

  objective: {
    level: 0.8,
    maxPerWindow: 3,
    layers: [
      { kind: "tone", freq: 880, wave: "sine", duration: 0.1, gain: 0.2 },
      { kind: "tone", freq: 1320, wave: "sine", duration: 0.14, gain: 0.15, delay: 0.06 },
    ],
  },

  /** Neglect just cost a card. A dull slap — the sound of paperwork landing. */
  polluted: {
    level: 0.85,
    layers: [
      { kind: "noise", freq: 900, duration: 0.07, gain: 0.4, filter: { type: "lowpass", freq: 1400 } },
      { kind: "tone", freq: 230, freqEnd: 170, wave: "triangle", duration: 0.26, gain: 0.22, delay: 0.03 },
    ],
  },

  runCleared: {
    level: 1,
    layers: [
      { kind: "tone", freq: 523, wave: "triangle", duration: 0.16, gain: 0.26 },
      { kind: "tone", freq: 659, wave: "triangle", duration: 0.16, gain: 0.26, delay: 0.13 },
      { kind: "tone", freq: 880, wave: "triangle", duration: 0.42, gain: 0.28, delay: 0.26 },
    ],
  },

  runFailed: {
    level: 1,
    layers: [
      { kind: "tone", freq: 330, wave: "sawtooth", duration: 0.22, gain: 0.24, filter: { type: "lowpass", freq: 1300 } },
      { kind: "tone", freq: 247, wave: "sawtooth", duration: 0.22, gain: 0.24, delay: 0.18, filter: { type: "lowpass", freq: 1100 } },
      { kind: "tone", freq: 165, wave: "sawtooth", duration: 0.6, gain: 0.26, delay: 0.36, filter: { type: "lowpass", freq: 900 } },
    ],
  },

  /** §3.1 grid contamination: a tile just took a chemical spill. Sharp attack
   * with hiss, falling tone underneath like a rupture and drain. */
  contaminated: {
    level: 0.8,
    layers: [
      { kind: "noise", freq: 2400, duration: 0.1, gain: 0.35, attack: 0.006, filter: { type: "highpass", freq: 1400 } },
      { kind: "tone", freq: 380, freqEnd: 150, wave: "square", duration: 0.25, gain: 0.25, filter: { type: "lowpass", freq: 1200 } },
    ],
  },

  /** §3.3 vermin risk counter tick: rising facility infestation. A rapid,
   * squeaky tick distinct from pressure's low-frequency thump. Throttled per
   * event since risk increments once per tick max. */
  verminRisk: {
    level: 0.65,
    layers: [
      { kind: "tone", freq: 1200, freqEnd: 1450, wave: "square", duration: 0.12, gain: 0.18, attack: 0.008, filter: { type: "highpass", freq: 800 } },
    ],
  },

  /** §3.3 rodent infestation: an infestable node's efficiency halved. Abrupt,
   * harsh sound — machinery failure — distinct from the organic poison register. */
  infested: {
    level: 0.75,
    layers: [
      { kind: "tone", freq: 280, freqEnd: 110, wave: "sawtooth", duration: 0.35, gain: 0.2, attack: 0.012, filter: { type: "lowpass", freq: 1000 } },
      { kind: "noise", freq: 1600, duration: 0.25, gain: 0.14, attack: 0.04, filter: { type: "bandpass", freq: 1600, q: 0.9 } },
    ],
  },

  /** §3.2 power surge freeze: electrical snap and breaker trip. Sharp
   * high-frequency crackle with a hard-falling tone, distinct register from
   * machinery-failure and low-frequency rumble. */
  powerOutage: {
    level: 0.9,
    layers: [
      { kind: "noise", freq: 4200, duration: 0.12, gain: 0.4, attack: 0.004, filter: { type: "highpass", freq: 2000 } },
      { kind: "tone", freq: 620, freqEnd: 180, wave: "square", duration: 0.28, gain: 0.28, filter: { type: "lowpass", freq: 1400 } },
    ],
  },

  /** §3.4 environmental disaster: heatwave stress or seismic jolt. Low-frequency
   * rumble bed paired with a sub-bass tone, conveying infrastructure stress.
   * Distinct from pressure's short thump and corrode's high-frequency grit. */
  structuralDisaster: {
    level: 0.8,
    layers: [
      { kind: "noise", freq: 300, duration: 0.5, gain: 0.35, filter: { type: "lowpass", freq: 600 } },
      { kind: "tone", freq: 85, freqEnd: 110, wave: "sine", duration: 0.55, gain: 0.22, filter: { type: "lowpass", freq: 400 } },
    ],
  },

  /**
   * §4's held-hazard-card penalty (spec 0007): a card already in hand just
   * cost something, again. This can repeat every turn a hazard card sits in
   * the hand, so it stays low-key (comparable level to pressure/corrode, not
   * burst/explode) — a dry click over a pair of close, slightly detuned square
   * tones (175/185 Hz) that beat against each other rather than ringing
   * cleanly, so it reads as "discordant cost" rather than a clean thump.
   * Distinct from polluted's soft lowpassed-noise paper slap (a card arriving)
   * — this is the card you're already holding costing you something, a
   * related but separate idea, so it is drier and shorter with no sweep.
   */
  hazardPenalty: {
    level: 0.5,
    layers: [
      { kind: "noise", freq: 1900, duration: 0.025, gain: 0.22, attack: 0.002, filter: { type: "bandpass", freq: 1900, q: 1.3 } },
      { kind: "tone", freq: 175, wave: "square", duration: 0.09, gain: 0.2, filter: { type: "lowpass", freq: 650 } },
      { kind: "tone", freq: 185, wave: "square", duration: 0.09, gain: 0.14, filter: { type: "lowpass", freq: 650 } },
    ],
  },

  /**
   * A clearsOnPlay hazard card (Clogged Line, Chemical Hazard Spill) just
   * released its lock (spec 0007) — the audio equivalent of the FX's mint
   * tint. A light mechanical click (the lock disengaging) under a single tone
   * that sweeps upward rather than stepping between two fixed pitches, so it
   * is distinct from objective's bright two-step chime and from merge's
   * simultaneous same-register layering — both of which stack two static
   * tones rather than sweep one. Reads as relief, not alarm.
   */
  hazardCleared: {
    level: 0.7,
    layers: [
      { kind: "noise", freq: 3000, duration: 0.02, gain: 0.18, attack: 0.002, filter: { type: "highpass", freq: 2200 } },
      { kind: "tone", freq: 500, freqEnd: 750, wave: "triangle", duration: 0.18, gain: 0.22, attack: 0.02, delay: 0.02 },
    ],
  },
};

export const CUE_NAMES = Object.keys(CUES) as readonly CueName[];
